import React from 'react';

import clsx from 'clsx';

import { REGEX } from '@lessonPlanner/regex';

import { useCharacterLimit } from '../../hooks/useCharacterLimit';

import { formClasses } from './utils/FormStyles';
import { Label } from './utils/Label';
import { SubLabel } from './utils/SubLabel';

interface TextFieldProps {
  id: string;
  label?: string;
  type?: string;
  maxLength?: number;
  disabled?: boolean;
  isMaxLengthReached?: boolean;
  onPointerDown?: (e: React.PointerEvent) => void;
  errorMessage?: string;
  subLabel?: string;
  className?: string;
  value?: string | number;
  placeholder?: string;
  labelClassName?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  [key: string]: unknown;
}

export function TextField({
  id,
  label,
  type = 'text',
  maxLength = 50,
  disabled = false,
  isMaxLengthReached = false,
  errorMessage = '',
  subLabel = '',
  labelClassName = '',
  ...props
}: TextFieldProps) {
  const {
    elementRef: inputRef,
    handleChange: handleLimitedChange,
    isAtOrOverMaxLength,
    effectiveMaxLength,
  } = useCharacterLimit<HTMLInputElement>({
    maxLength,
    onChange: props.onChange,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (!REGEX.VALID_TEXT_INPUT.test(inputValue)) {
      const cleanedInput = inputValue.replace(REGEX.INVALID_TEXT_INPUT, '');
      e.target.value = cleanedInput;
    }

    handleLimitedChange(e);
  };

  const focusNextTabbableElement = () => {
    const form = inputRef.current?.form;
    if (!form) return;
    const elements = form.elements;
    const currentIndex = Array.from(elements).findIndex(
      (el) => el === inputRef.current,
    );
    const nextIndex = currentIndex + 1;
    const nextElement = elements[nextIndex] as HTMLElement;
    if (nextElement) {
      nextElement.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      focusNextTabbableElement();
    }
  };

  const inputClassName = clsx(
    formClasses,
    { 'cursor-not-allowed': disabled },
    {
      'border border-red-500 focus:border-red-700':
        isMaxLengthReached || errorMessage,
    },
  );

  const isCurrentlyAtOrOverMaxLength = isAtOrOverMaxLength(props.value);

  return (
    <div className={props.className}>
      {label && (
        <Label className={labelClassName} id={id}>
          {label}
        </Label>
      )}
      {subLabel && <SubLabel id={id}>{subLabel}</SubLabel>}
      <input
        id={id}
        type={type}
        {...props}
        ref={inputRef}
        className={inputClassName}
        disabled={disabled}
        maxLength={effectiveMaxLength}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
      />
      {(isMaxLengthReached || isCurrentlyAtOrOverMaxLength) && (
        <p className="text-red-500">You have reached the character limit</p>
      )}
      {errorMessage && <p className="text-red-500">{errorMessage}</p>}
    </div>
  );
}
